/**
 * HLOS-168-B: Vault backend "memory" — storage in-memory para testes
 * vitest (jsdom não tem OPFS nem Swift bridge).
 *
 * NUNCA usar em produção — dados são perdidos a cada cold start do app
 * (CL-07 violation se produção usar MemoryBackend).
 *
 * Constitucional:
 * - CL-07 (vault local-first): apenas para testes — não viola
 *   (VaultStore valida que MemoryBackend não está em produção).
 * - LGPD: testes usam dados sintéticos (vault-crypto skill §9).
 */

import type { VaultStorage } from "../store";

/**
 * Backend in-memory para testes. Cada backend instance tem seu próprio
 * Map isolado (sem cross-test pollution).
 */
export class MemoryBackend implements VaultStorage {
  private readonly files = new Map<string, Uint8Array>();

  async read(relativePath: string): Promise<Uint8Array | null> {
    const data = this.files.get(relativePath);
    return data ? new Uint8Array(data) : null;
  }

  async write(relativePath: string, data: Uint8Array): Promise<void> {
    this.files.set(relativePath, new Uint8Array(data));
  }

  async list(directory: string): Promise<string[]> {
    const prefix = directory ? directory.replace(/\/$/, "") + "/" : "";
    return Array.from(this.files.keys())
      .filter((k) => k.startsWith(prefix))
      .map((k) => k.slice(prefix.length))
      .filter((k) => !k.includes("/")) // apenas direct children
      .sort();
  }

  async remove(relativePath: string): Promise<void> {
    this.files.delete(relativePath);
  }

  /** Helper para testes: limpar todos os arquivos. */
  clear(): void {
    this.files.clear();
  }

  /** Helper para testes: contar arquivos. */
  size(): number {
    return this.files.size;
  }
}
